import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { showSuccess } from '@/components/common/Toast';

export interface GeneralMultiplier {
  id: string;
  scope: 'global' | 'capability';
  capabilityId: string | null;
  code: string;
  name: string;
  description: string | null;
  multiplier: string; // e.g., "+0.3x to all implementation"
  appliesTo: string[] | null;
  inputType: 'number' | 'select' | 'boolean';
  baselineValue: number | null;
  multiplierFactor: number | null;
  multiplierStep: number | null;
  multiplierLabel: string | null;
  optionFactors: Record<string, number> | null;
  options: string[] | null;
  defaultValue: string | null;
  minValue: number | null;
  maxValue: number | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

interface MultipliersResponse {
  multipliers: GeneralMultiplier[];
}

interface CreateMultiplierInput {
  code: string;
  name: string;
  description?: string;
  multiplier: string;
  appliesTo?: string[];
  inputType?: 'number' | 'select' | 'boolean';
  baselineValue?: number;
  multiplierFactor?: number;
  multiplierStep?: number;
  multiplierLabel?: string;
  optionFactors?: Record<string, number>;
  options?: string[];
  defaultValue?: string;
  minValue?: number;
  maxValue?: number;
  sortOrder?: number;
  isActive?: boolean;
}

interface UpdateMultiplierInput extends Partial<CreateMultiplierInput> {
  id: string;
}

/**
 * Fetch all general multipliers
 */
export function useGeneralMultipliers(includeInactive = false) {
  return useQuery({
    queryKey: ['vcf', 'general-multipliers', { includeInactive }],
    queryFn: () =>
      api.get<MultipliersResponse>(
        `/vcf/general-multipliers${includeInactive ? '?includeInactive=true' : ''}`
      ),
  });
}

/**
 * Fetch a single general multiplier
 */
export function useGeneralMultiplier(id: string) {
  return useQuery({
    queryKey: ['vcf', 'general-multipliers', id],
    queryFn: () => api.get<{ multiplier: GeneralMultiplier }>(`/vcf/general-multipliers/${id}`),
    enabled: !!id,
  });
}

/**
 * Create a new general multiplier (admin only)
 */
export function useCreateMultiplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMultiplierInput) =>
      api.post<{ multiplier: GeneralMultiplier }>('/vcf/general-multipliers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vcf', 'general-multipliers'] });
      showSuccess('Multiplier created');
    },
  });
}

/**
 * Update a general multiplier (admin only)
 */
export function useUpdateMultiplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateMultiplierInput) =>
      api.patch<{ multiplier: GeneralMultiplier }>(`/vcf/general-multipliers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vcf', 'general-multipliers'] });
      showSuccess('Multiplier updated');
    },
  });
}

/**
 * Delete a general multiplier (admin only)
 */
export function useDeleteMultiplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/vcf/general-multipliers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vcf', 'general-multipliers'] });
      showSuccess('Multiplier deleted');
    },
  });
}
